import React, { useEffect, useState } from 'react';
import styles from './AdminShared.module.scss';
import {
    AdminLocationCreateDto,
    AdminLocationDto
} from '../../../types/admin.types';
import {
    archiveAdminLocation,
    createAdminLocation,
    fetchAdminLocations,
    restoreAdminLocation,
    updateAdminLocation
} from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';
import VenueFormFields, {
    emptyVenueForm,
    normalizeVenueCreatePayload
} from './components/VenueFormFields';

const mapLocation = (item: any): AdminLocationDto => ({
    id: item.id ?? item.Id ?? '',
    name: item.name ?? item.Name ?? '',
    city: item.city ?? item.City ?? null,
    region: item.region ?? item.Region ?? null,
    country: item.country ?? item.Country ?? null,
    venueUrl: item.venueUrl ?? item.VenueUrl ?? null,
    isArchived: item.isArchived ?? item.IsArchived ?? false,
    createdDate: item.createdDate ?? item.CreatedDate ?? '',
    updatedDate: item.updatedDate ?? item.UpdatedDate ?? ''
});

const AdminLocations = () => {
    const { getUserInfo } = useUserState();
    const token = getUserInfo()?.token;
    const [locations, setLocations] = useState<AdminLocationDto[]>([]);
    const [showArchived, setShowArchived] = useState(false);
    const [form, setForm] = useState<AdminLocationCreateDto>(emptyVenueForm);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const loadLocations = async () => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const response = await fetchAdminLocations(showArchived, token);
            setLocations((response ?? []).map((item: any) => mapLocation(item)));
        } catch (error) {
            setErrorMessage('Unable to load locations.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadLocations();
    }, [showArchived]);

    const resetForm = () => {
        setForm(emptyVenueForm);
        setSelectedId(null);
    };

    const handleEdit = (item: AdminLocationDto) => {
        setSelectedId(item.id);
        setForm({
            name: item.name,
            city: item.city ?? '',
            region: item.region ?? '',
            country: item.country ?? '',
            venueUrl: item.venueUrl ?? ''
        });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage(null);
        const payload = normalizeVenueCreatePayload(form);
        if (!payload.name) {
            setErrorMessage('Venue name is required.');
            return;
        }

        try {
            if (selectedId) {
                await updateAdminLocation(selectedId, payload, token);
            } else {
                await createAdminLocation(payload, token);
            }
            await loadLocations();
            resetForm();
        } catch (error) {
            setErrorMessage('Unable to save location.');
        }
    };

    const handleArchiveToggle = async (item: AdminLocationDto) => {
        setErrorMessage(null);
        try {
            if (item.isArchived) {
                await restoreAdminLocation(item.id, token);
            } else {
                await archiveAdminLocation(item.id, token);
            }

            if (selectedId === item.id) {
                resetForm();
            }

            await loadLocations();
        } catch (error) {
            setErrorMessage('Unable to update archive status.');
        }
    };

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1>Venues</h1>
                    <p>Manage event venues and their metadata.</p>
                </div>
                <div className={styles.actions}>
                    <label>
                        <input
                            type="checkbox"
                            checked={showArchived}
                            onChange={(e) => setShowArchived(e.target.checked)}
                        />
                        {' '}Show archived
                    </label>
                </div>
            </header>

            {errorMessage ? <div className={styles.muted}>{errorMessage}</div> : null}

            <div className={styles.content}>
                <section className={styles.panel}>
                    <h2>Venue List</h2>
                    {isLoading ? (
                        <div className={styles.muted}>Loading venues...</div>
                    ) : locations.length === 0 ? (
                        <div className={styles.empty}>No venues found.</div>
                    ) : (
                        locations.map((item) => (
                            <div key={item.id} className={styles.listItem}>
                                <div>
                                    <strong>{item.name}</strong>
                                    <div className={styles.muted}>
                                        {[item.city, item.region, item.country].filter(Boolean).join(', ') || 'No location metadata'}
                                        {item.isArchived ? ' • Archived' : ''}
                                    </div>
                                </div>
                                <div className={styles.rowActions}>
                                    <button type="button" className={styles.secondary} onClick={() => handleEdit(item)}>Edit</button>
                                    <button type="button" className={styles.danger} onClick={() => handleArchiveToggle(item)}>
                                        {item.isArchived ? 'Restore' : 'Archive'}
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </section>

                <section className={styles.panel}>
                    <h2>{selectedId ? 'Edit Venue' : 'New Venue'}</h2>
                    <form onSubmit={handleSubmit}>
                        <VenueFormFields
                            form={form}
                            onChange={setForm}
                            fieldClassName={styles.field}
                            idPrefix="location"
                        />
                        <div className={styles.actions}>
                            <button className={styles.primary} type="submit">
                                {selectedId ? 'Update Venue' : 'Create Venue'}
                            </button>
                            {selectedId ? (
                                <button type="button" className={styles.secondary} onClick={resetForm}>Cancel</button>
                            ) : null}
                        </div>
                    </form>
                </section>
            </div>
        </div>
    );
};

export default AdminLocations;
